/**
 * New Relic tracking utilities for Passkey MFA drawer and remote purchases
 * Tracks success/failure rates for passkey authentication flows
 */

import { newrelic } from './newrelic';

export const PASSKEY_MFA_TRACKING_ACTIONS = {
  MFA_FLOW_INITIATED: 'passkey-mfa-flow-initiated',
  MFA_FLOW_SUCCESS: 'passkey-mfa-flow-success',
  MFA_FLOW_FAILURE: 'passkey-mfa-flow-failure',
  REMOTE_PURCHASES_AUTH_START: 'remote-purchases-auth-start',
  REMOTE_PURCHASES_AUTH_SUCCESS: 'remote-purchases-auth-success',
  REMOTE_PURCHASES_AUTH_FAILURE: 'remote-purchases-auth-failure',
  T2C_ENROLLMENT_INITIATED: 't2c-enrollment-initiated',
  T2C_ENROLLMENT_SUCCESS: 't2c-enrollment-success',
  T2C_ENROLLMENT_FAILURE: 't2c-enrollment-failure',
  USER_PERMISSIONS_UPDATE_INITIATED: 'user-permissions-update-initiated',
  USER_PERMISSIONS_UPDATE_SUCCESS: 'user-permissions-update-success',
  USER_PERMISSIONS_UPDATE_FAILURE: 'user-permissions-update-failure',
} as const;

export interface IPasskeyMfaTrackingData {
  [key: string]: string | number | boolean | null | undefined;
}

export interface IPasskeyAuthFailure extends IPasskeyMfaTrackingData {
  errorCode: string;
  errorMessage?: string;
  httpStatus?: number;
  flowType?: string;
}

export interface IRemotePurchasesAuthData extends IPasskeyMfaTrackingData {
  cardType?: string;
  locType?: string;
  approversCount?: number;
}

/**
 * Track MFA flow initiation
 */
export const trackMfaFlowInitiated = (
  flowType: string,
  metadata?: IPasskeyMfaTrackingData,
) => {
  const payload = {
    ...metadata,
    flowType,
    timestamp: new Date().toISOString(),
  };
  newrelic.addPageAction(
    PASSKEY_MFA_TRACKING_ACTIONS.MFA_FLOW_INITIATED,
    payload,
  );
};

/**
 * Track successful MFA flow completion
 */
export const trackMfaFlowSuccess = (
  flowType: string,
  metadata?: IPasskeyMfaTrackingData,
) => {
  const payload = {
    ...metadata,
    flowType,
    result: 'success',
    timestamp: new Date().toISOString(),
  };
  newrelic.addPageAction(
    PASSKEY_MFA_TRACKING_ACTIONS.MFA_FLOW_SUCCESS,
    payload,
  );
};

/**
 * Track MFA flow failure
 */
export const trackMfaFlowFailure = (
  flowType: string,
  failureData: IPasskeyAuthFailure,
) => {
  const payload = {
    ...failureData,
    flowType,
    result: 'failure',
    timestamp: new Date().toISOString(),
  };
  newrelic.addPageAction(
    PASSKEY_MFA_TRACKING_ACTIONS.MFA_FLOW_FAILURE,
    payload,
  );
};

/**
 * Track remote purchases authentication initiation
 */
export const trackRemotePurchasesAuthStart = (
  data: IRemotePurchasesAuthData,
) => {
  const payload = { ...data, timestamp: new Date().toISOString() };
  newrelic.addPageAction(
    PASSKEY_MFA_TRACKING_ACTIONS.REMOTE_PURCHASES_AUTH_START,
    payload,
  );
};

/**
 * Track successful remote purchases authentication
 */
export const trackRemotePurchasesAuthSuccess = (
  data: IRemotePurchasesAuthData,
) => {
  const payload = {
    ...data,
    result: 'success',
    timestamp: new Date().toISOString(),
  };
  newrelic.addPageAction(
    PASSKEY_MFA_TRACKING_ACTIONS.REMOTE_PURCHASES_AUTH_SUCCESS,
    payload,
  );
};

/**
 * Track failed remote purchases authentication
 */
export const trackRemotePurchasesAuthFailure = (
  data: IRemotePurchasesAuthData & IPasskeyAuthFailure,
) => {
  const payload = {
    ...data,
    result: 'failure',
    timestamp: new Date().toISOString(),
  };
  newrelic.addPageAction(
    PASSKEY_MFA_TRACKING_ACTIONS.REMOTE_PURCHASES_AUTH_FAILURE,
    payload,
  );
};

/**
 * Track T2C enrollment initiation
 */
export const trackT2cEnrollmentInitiated = (
  metadata?: IPasskeyMfaTrackingData,
) => {
  const payload = { ...metadata, timestamp: new Date().toISOString() };
  newrelic.addPageAction(
    PASSKEY_MFA_TRACKING_ACTIONS.T2C_ENROLLMENT_INITIATED,
    payload,
  );
};

/**
 * Track successful T2C enrollment
 */
export const trackT2cEnrollmentSuccess = (
  metadata?: IPasskeyMfaTrackingData,
) => {
  const payload = {
    ...metadata,
    result: 'success',
    timestamp: new Date().toISOString(),
  };
  newrelic.addPageAction(
    PASSKEY_MFA_TRACKING_ACTIONS.T2C_ENROLLMENT_SUCCESS,
    payload,
  );
};

/**
 * Track T2C enrollment failure
 */
export const trackT2cEnrollmentFailure = (failureData: IPasskeyAuthFailure) => {
  const payload = {
    ...failureData,
    result: 'failure',
    timestamp: new Date().toISOString(),
  };
  newrelic.addPageAction(
    PASSKEY_MFA_TRACKING_ACTIONS.T2C_ENROLLMENT_FAILURE,
    payload,
  );
};

/**
 * Track user permissions update initiation
 */
export const trackUserPermissionsUpdateInitiated = (
  metadata?: IPasskeyMfaTrackingData,
) => {
  const payload = { ...metadata, timestamp: new Date().toISOString() };
  newrelic.addPageAction(
    PASSKEY_MFA_TRACKING_ACTIONS.USER_PERMISSIONS_UPDATE_INITIATED,
    payload,
  );
};

/**
 * Track successful user permissions update
 */
export const trackUserPermissionsUpdateSuccess = (
  usersCount: number,
  metadata?: IPasskeyMfaTrackingData,
) => {
  const payload = {
    ...metadata,
    result: 'success',
    usersCount,
    timestamp: new Date().toISOString(),
  };
  newrelic.addPageAction(
    PASSKEY_MFA_TRACKING_ACTIONS.USER_PERMISSIONS_UPDATE_SUCCESS,
    payload,
  );
};

/**
 * Track user permissions update failure
 */
export const trackUserPermissionsUpdateFailure = (
  failureData: IPasskeyAuthFailure,
) => {
  const payload = {
    ...failureData,
    result: 'failure',
    timestamp: new Date().toISOString(),
  };
  newrelic.addPageAction(
    PASSKEY_MFA_TRACKING_ACTIONS.USER_PERMISSIONS_UPDATE_FAILURE,
    payload,
  );
};
